import React, { forwardRef, useImperativeHandle } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';

import TouchableElement from '@components/common/touchableElement';

import { Alert } from '@helpers';
import { fontOP } from '@styles/fonts';
import { scaleOP } from '@components/frame';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';

interface CancelButtonProps {
  updating?: boolean;
  onDiscard: () => void;
  C: ColorFunction;
  onSave?: () => void;
  skipAlert?: boolean;
  font?: typeof fontOP;
  scale?: ScaleFunction;
}

const CancelButton = forwardRef<() => void, CancelButtonProps>(
  ({ onDiscard, C, updating, onSave, font = fontOP, scale = scaleOP, skipAlert }, ref) => {
    const onClick = () => {
      if (skipAlert) {
        return onDiscard();
      }

      Alert({
        title: i18n.t(updating ? 'Discard Changes' : 'Delete Note') + '?',
        text: i18n.t(
          updating
            ? 'You’ve made changes to this note. If you discard now, your edits will be lost.'
            : 'Do you want to delete this new note? It hasn’t been saved and will be lost if you delete it.'
        ),
        actions: [
          {
            text: i18n.t(updating ? 'Discard Changes' : 'Delete Note'),
            onPress: onDiscard,
            style: 'danger',
          },
          {
            text: i18n.t('Save Note'),
            onPress: onSave,
            style: 'secondary',
          },
          {
            text: i18n.t('Keep Editing'),
            style: 'primary',
            onPress: () => {},
          },
        ],
        position: 'vertical',
      });
    };

    useImperativeHandle(ref, () => onClick);

    return (
      <TouchableElement onPress={onClick}>
        {({ boxStyle, pressHoverColor }) => {
          return (
            <View
              style={[
                boxStyle,
                {
                  paddingHorizontal: scale(13),
                  paddingVertical: scale(6.5),
                  backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                  borderRadius: scale(14.5),
                },
              ]}
            >
              <Text style={[font['Button 2'], { color: C('Button/Light/Secondary Text') }]}>{i18n.t('Cancel')}</Text>
            </View>
          );
        }}
      </TouchableElement>
    );
  }
);

export default CancelButton;
